// File: src/pages/CategoryList.jsx
// Premium ReyZa Design - Liste des Categories Fully Responsive (Version Francaise)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Tag, ArrowRight, Package } from 'lucide-react';
import './css/CategoryList.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const CategoryList = ({ openCart }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE}/categories`);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
        }
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        console.error('Erreur de recuperation des categories :', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <div className="catlist-loading">
        <div className="catlist-loading-spinner"></div>
        <p>Chargement des categories...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="catlist-error">
        <Package size={64} className="catlist-error-icon" />
        <h2 className="catlist-error-title">Impossible de charger les categories</h2>
        <p className="catlist-error-message">{error}</p>
        <p className="catlist-error-hint">
          Veuillez verifier que votre serveur backend est en cours d'execution et accessible.
        </p>
        <button 
          className="catlist-error-retry"
          onClick={() => window.location.reload()}
        >
          Reessayer
        </button>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="catlist-empty">
        <Tag size={64} className="catlist-empty-icon" />
        <h2 className="catlist-empty-title">Aucune categorie disponible</h2>
        <p className="catlist-empty-text">Revenez bientot pour decouvrir de nouvelles categories !</p>
      </div>
    );
  }

  return (
    <div className="catlist-container">
      {/* En-tete */}
      <div className="catlist-header">
        <div className="catlist-header-badge">
          <Tag size={16} />
          <span>Categories</span>
        </div>
        <h1 className="catlist-title">Decouvrez nos categories</h1>
        <p className="catlist-subtitle">
          Parcourez nos {categories.length} {categories.length === 1 ? 'categorie' : 'categories'} soigneusement selectionnees de produits premium
        </p>
      </div>

      {/* Grille des categories */}
      <div className="catlist-grid">
        {categories.map((category) => (
          <div
            key={category.id}
            className="catlist-card"
            onClick={() => navigate(`/category/${category.slug || category.id}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/category/${category.slug || category.id}`);
              }
            }}
          >
            <div className="catlist-card-image-wrapper">
              {category.imageUrl ? (
                <img
                  src={category.imageUrl}
                  alt={category.name}
                  className="catlist-card-image"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.classList.add('no-image');
                  }}
                />
              ) : (
                <div className="catlist-card-placeholder">
                  <Tag size={48} />
                </div>
              )}
            </div>
            
            <div className="catlist-card-content">
              <h2 className="catlist-card-name">{category.name}</h2>
              {category.description && (
                <p className="catlist-card-description">{category.description}</p>
              )}
              <div className="catlist-card-action">
                <span className="catlist-card-link">Voir la collection</span>
                <ArrowRight size={18} className="catlist-card-arrow" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryList;
